import { isAxiosError } from 'axios';
import { toValue } from 'vue';
import { client, project as projectGetter, user as userGetter } from './plugin';

export interface HasAccessOptions {
  maxAge?: number;
}

export interface AccessResolutions {
  roles?: { id: string; name: string }[];
}

export interface PermissionCache {
  user: string;
  project: string;
  permission: string;
  outcome: Promise<{
    action: 'granted' | 'denied';
    resolutions?: AccessResolutions;
  }>;
  expires: number;
}

const withResolvers = <T>() => {
  let resolve: (value: T) => void = () => {};
  let reject: (error: any) => void = () => {};

  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });

  return { resolve, reject, promise };
};

const permissionsCache: Record<string, PermissionCache> = {};

const setCached = (
  project: string,
  user: string,
  permission: string,
  outcome: Promise<{
    action: 'granted' | 'denied';
    resolutions?: AccessResolutions;
  }>,
) => {
  return (permissionsCache[`${project}|${user}|${permission}`] = {
    user,
    project,
    permission,
    outcome,
    expires: Date.now() + 10000,
  });
};

const getCached = (project: string, user: string, permission: string) => {
  const cache = permissionsCache[`${project}|${user}|${permission}`];

  if ((cache?.expires || 0) < Date.now()) {
    return undefined;
  }

  return cache;
};

export const checkAccess = async (
  permissions: string[],
): Promise<{
  access: 'pending' | 'granted' | 'denied';
  permissions: {
    granted: string[];
    denied: string[];
  };
  resolutions?: AccessResolutions;
}> => {
  if (import.meta.env.VITE_AUTHORIZER_MODE === 'debug') {
    return Promise.resolve({
      access: 'granted',
      permissions: {
        granted: permissions,
        denied: [],
      },
    });
  } else if (import.meta.env.VITE_AUTHORIZER_MODE === 'debug-deny') {
    return Promise.resolve({
      access: 'denied',
      permissions: {
        granted: [],
        denied: permissions,
      },
    });
  }

  const user = toValue(userGetter);
  const project = toValue(projectGetter);

  if (project === undefined || user === undefined) {
    return {
      access: 'denied',
      permissions: {
        granted: [],
        denied: [],
      },
    };
  }

  const permissionStates = permissions.map((permission) => ({
    permission,
    cache: getCached(project, user, permission),
  }));

  const unCachedPermissions = permissionStates
    .filter((state) => state.cache === undefined)
    .map((state) => {
      const resolvers = withResolvers<{
        action: 'granted' | 'denied';
        resolutions?: AccessResolutions;
      }>();

      setCached(project, user, state.permission, resolvers.promise);

      return { permission: state.permission, resolve: resolvers.resolve };
    });

  if (unCachedPermissions.length > 0) {
    await client
      .enforce({
        enforceRequest: {
          project,
          user,
          permissions,
        },
      })
      .then(({ data }) => {
        unCachedPermissions.forEach(({ permission, resolve }) => {
          if (data.permissions.granted.includes(permission)) {
            resolve({
              action: 'granted',
            });
          } else {
            resolve({
              action: 'denied',
            });
          }
        });
      })
      .catch((error) => {
        if (isAxiosError(error) && error.response?.status === 403) {
          unCachedPermissions.forEach(({ permission, resolve }) => {
            if (error.response?.data.permissions.granted.includes(permission)) {
              resolve({
                action: 'granted',
              });
            } else {
              resolve({
                action: 'denied',
                resolutions: error.response?.data.resolutions,
              });
            }
          });
        } else {
          throw error;
        }
      });
  }

  const resolvedPermissions = await Promise.all(
    permissions.map(async (permission) => {
      const cache = getCached(project, user, permission);

      const outcome = await cache?.outcome;

      return {
        permission,
        action: outcome?.action,
        resolutions: outcome?.resolutions,
      };
    }),
  );

  const granted = resolvedPermissions
    .filter((permission) => permission?.action === 'granted')
    .filter((p) => p !== undefined)
    .map((p) => p?.permission);
  const denied = resolvedPermissions
    .filter((permission) => permission?.action !== 'granted')
    .filter((p) => p !== undefined)
    .map((p) => p?.permission);

  const resolutions: AccessResolutions = {
    roles: resolvedPermissions
      .filter((p) => p.resolutions !== undefined)
      .flatMap((p) => p.resolutions?.roles)
      .filter((r) => r !== undefined),
  };

  return {
    access: denied.length === 0 ? 'granted' : 'denied',
    permissions: {
      granted,
      denied,
    },
    resolutions,
  };
};
